import React, { useState, useEffect } from 'react';
import { Stage, Layer, Text, Image, Group } from 'react-konva';
import { Icon } from '@iconify/react';
import { Button, ButtonGroup } from '@mui/material';
import { useSocket } from '../socket';
import { useTable } from '../contexts/TableContext';
import { useFloor } from '../contexts/FloorContext';
import { useAuth } from '../contexts/AuthContext';
import { floorNav } from '../components/NavigationPath';
import Layout2 from '../components/Layout2';
import AddFloor from '../components/AddFloor';
import AddTable from '../components/AddTable';
import PageHeader from '../components/PageHeader';
import useImage from 'use-image';
import { useMemo } from 'react';

const AVAILABLE = 'available';
const OCCUPIED = 'occupied';
const ISBILLING = 'is_billing';

function CreateFloor() {
  const { getAllFloors, getSingleFloor } = useFloor();
  const { updateTable } = useTable();
  const { restaurantId, auth, hasPermission } = useAuth();
  const socket = useSocket();

  const canCreate = useMemo(() => hasPermission("table-floor", "create"),  [hasPermission]);
  const canUpdate = useMemo(() => hasPermission("table-floor", "update"), [hasPermission]);
  const canDelete = useMemo(() => hasPermission("table-floor", "delete"), [hasPermission]);

  const [floorImage] = useImage('/images/floor.jpg');

  const [shapes, setShapes] = useState([]);
  const [floorId, setFloorId] = useState(null);
  const [editFloorId, setEditFloorId] = useState(null);
  const [editTableId, setEditTableId] = useState(null);
  const [visible, setVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [floorListVisible, setFloorListVisible] = useState(false);
  const [tableCreateVisible, setTableCreateVisible] = useState(false);
  const [floorList, setFloorList] = useState([]);
  const [floorName, setFloorName] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hoveredShapeId, setHoveredShapeId] = useState(null);
  const [draggedShapeId, setDraggedShapeId] = useState(null);
  const [stageSize, setStageSize] = useState({
    width: window.innerWidth - 630,
    height: window.innerHeight * 0.7,
  });
  const [tableShapeProperties, setTableShapeProperties] = useState({
    width: 150,
    height: 150,
    rotation: null,
    color: null,
    name: '',
    shape: 'circle',
    x: null,
    y: null,
    chairs: '',
    size: 'regular'
  });
  const [floorDetail, setFloorDetail] = useState({
    name: '',
    restaurant_id: null,
  });

  const [circleTableAvailableImage] = useImage('/images/circle_table_available.png');
  const [circleTableOccupiedImage] = useImage('/images/circle_table_occupied.png');
  const [circleTableIsBillingImage] = useImage('/images/circle_table_is_billing.png');

  const [squareTableAvailableImage] = useImage('/images/square_table_available.png');
  const [squareTableOccupiedImage] = useImage('/images/square_table_occupied.png');
  const [squareTableIsBillingImage] = useImage('/images/square_table_is_billing.png');

  const [rectangleTableAvailableImage] = useImage('/images/rectangle_table_available.png');
  const [rectangleTableOccupiedImage] = useImage('/images/rectangle_table_occupied.png');
  const [rectangleTableIsBillingImage] = useImage('/images/rectangle_table_is_billing.png');

  const handleTableImages = (shape, status) => {
    const images = {
      circle: {
        [AVAILABLE]: circleTableAvailableImage,
        [OCCUPIED]: circleTableOccupiedImage,
        [ISBILLING]: circleTableIsBillingImage
      },
      square: {
        [AVAILABLE]: squareTableAvailableImage,
        [OCCUPIED]: squareTableOccupiedImage,
        [ISBILLING]: squareTableIsBillingImage
      },
      rectangle: {
        [AVAILABLE]: rectangleTableAvailableImage,
        [OCCUPIED]: rectangleTableOccupiedImage,
        [ISBILLING]: rectangleTableIsBillingImage
      }
    };

    return images[shape]?.[status] || null;
  };

  const fetchFloors = async () => {
    try {
      setIsLoading(true);
      const data = await getAllFloors();
      if (data.length > 0) {
        setFloorId(data[0].id);
      }
      setFloorList(data);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching floors', error);
    }
  };

  useEffect(() => {
    if (restaurantId) {
      fetchFloors();
    }
  }, [restaurantId]);

  useEffect(() => {
    const handleTableChange = (tableData) => {
      const updatedTable = {
        ...tableData,
        draggable: true,
        x: (tableData.x * stageSize.width) / 100,
        y: (tableData.y * stageSize.height) / 100,
      };

      setShapes((prevShapes) => {
        return prevShapes.map((shape) =>
          shape.id === updatedTable.id ? updatedTable : shape
        );
      });
    };

    socket.on('tableChangeSend', handleTableChange);

    return () => {
      socket.off('tableChangeSend', handleTableChange);
    };
  }, [socket, stageSize.width, stageSize.height, setShapes]);

  const fetchSingleFloor = async () => {
    try {
      const data = await getSingleFloor(floorId);
      setFloorName(data.floor.name);

      if (data.tables.length !== 0) {
        const formattedTables = data.tables.map((table) => {
          return {
            ...table,
            draggable: true,
            x: (table.x * stageSize.width) / 100,
            y: (table.y * stageSize.height) / 100,
          };
        });
        setShapes(formattedTables);
      } else {
        setShapes([]);
      }
    } catch (error) {
      console.error('Error fetching single floor:', error);
    }
  };

  useEffect(() => {
    if (floorId) {
      fetchSingleFloor();
    } else {
      setShapes([]);
      setFloorName(null);
      setTableShapeProperties({
        width: 150,
        height: 150,
        rotation: null,
        color: null,
        name: '',
        shape: 'circle',
        x: null,
        y: null,
        chairs: '',
        size: 'regular'
      });
    }
  }, [floorId]);

  useEffect(() => {
    function handleResize() {
      setStageSize({
        width: window.innerWidth - 630,
        height: window.innerHeight * 0.7,
      });
    }

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleDragEnd = async (index, x, y) => {
    setDraggedShapeId(null);
    const updatedShapes = [...shapes];
    updatedShapes[index] = { ...updatedShapes[index], x, y };
    const table = updatedShapes[index];
    x = (x * 100) / stageSize.width;
    y = (y * 100) / stageSize.height;

    // socket.emit('tableChangeReceive', { ...table, x, y });

    await updateTable(table.id, { ...table, x, y });
    setShapes(updatedShapes);
  };

  // const toggleFloorList = () => {
  //   if (floorListVisible) {
  //     setFloorListVisible(false)
  //   } else {
  //     setFloorListVisible(true);
  //     setTableCreateVisible(false);
  //   }
  // };

  const toggleTableCreate = () => {
    if (tableCreateVisible) {
      setTableCreateVisible(false);
      setTableShapeProperties({
        width: 150,
        height: 150,
        rotation: null,
        color: null,
        name: '',
        shape: 'circle',
        x: null,
        y: null,
        chairs: '',
        size: 'regular'
      });
    } else if (editTableId) {
      setEditTableId(null);
      setTableShapeProperties({
        width: 150,
        height: 150,
        rotation: null,
        color: null,
        name: '',
        shape: 'circle',
        x: null,
        y: null,
        chairs: '',
        size: 'regular'
      });
    } else {
      setFloorListVisible(false);
      setTableCreateVisible(true);
    }
  };

  function truncateText(text, maxLength) {
    if (text.length > maxLength) {
      return text.substring(0, maxLength - 1) + '...';
    } else {
      return text;
    }
  }  

  return (
    <Layout2 items={floorNav.items} navTitle={floorNav.header}>
      {(visible || (editMode && editFloorId)) && (
        <AddFloor
          visible={visible}
          editMode={editMode}
          editFloorId={editFloorId}
          floorDetail={floorDetail}
          setFloorId={setFloorId}
          setVisible={setVisible}
          setEditMode={setEditMode}
          fetchFloors={fetchFloors}
          setEditFloorId={setEditFloorId}
          setFloorDetail={setFloorDetail}
          canCreate={canCreate}
          canUpdate={canUpdate}
          canDelete={canDelete}
        />
      )}
      <div className="d-flex flex-column w-100">
        <PageHeader title={'Floorplan Builder'} />
        <div className="d-flex floor">
          <div className="floor-sidebar">
            {/* <div className='d-flex justify-content-between mt-2 floor-list' onClick={() => toggleFloorList()}>
              Floors
              <Icon icon="clarity:edit-solid" style={{ color: 'black' }} />
            </div>
            {
              floorListVisible && (
                <div className='floor-cards'>
                  <Card
                    className='add-floor'
                    header={<Icon icon="tabler:plus" className='add-floor-icon' height={70} width={70} />}
                  />
                  <FloorCard color={'green'} />
                  <FloorCard image={'/images/a.jpg'} />
                  <FloorCard color={'red'} />
                  <FloorCard color={'yellow'} />
                  <FloorCard color={'gray'} />
                  <FloorCard color={'green'} />
                  <FloorCard color={'green'} />
                </div>
              )} */}
            <div className="d-flex justify-content-between mt-2 floor-list" onClick={() => {if(canCreate) toggleTableCreate()}}>
              Tables
              <Icon icon="clarity:edit-solid" style={{ color: 'black', marginTop: '5px' }} />
            </div>
            {(tableCreateVisible || editTableId) && floorId && (
              <AddTable
                shapes={shapes}
                floorId={floorId}
                stageSize={stageSize}
                editTableId={editTableId}
                tableCreateVisible={tableCreateVisible}
                tableShapeProperties={tableShapeProperties}
                setShapes={setShapes}
                setEditTableId={setEditTableId}
                fetchSingleFloor={fetchSingleFloor}
                setTableShapeProperties={setTableShapeProperties}
                canCreate={canCreate}
                canUpdate={canUpdate}
                canDelete={canDelete}
              />
            )}
          </div>
          <div className="floor-right-container">
            {restaurantId ? (
              <div className="floor-header d-flex border-bottom">
                {canCreate && 
                  <Button
                    variant="contained"
                    color="inherit"
                    className="me-3"
                    onClick={() => setVisible(true)}
                    title="Add Floor"
                  >
                    <Icon icon="mingcute:add-fill" width={25} />
                  </Button>
                }
                {floorList && floorList.length !== 0 && floorList.map((floor) => (
                  <div key={floor.id} className="me-2">
                    <ButtonGroup variant="contained" color="inherit">
                      <Button
                        title={floor.name}
                        onClick={() => {
                          setFloorId(floor.id);
                          setEditTableId(null);
                          setTableShapeProperties({
                            width: 150,
                            height: 150,
                            rotation: null,
                            color: null,
                            name: '',
                            shape: 'circle',
                            x: null,
                            y: null,
                            chairs: '',
                          });
                        }}
                      >
                        <div className="floor_name_btn">{floor.name}</div>
                      </Button>
                      {(canUpdate || canDelete) && 
                        <Button
                          title="Edit"
                          onClick={() => {
                            setEditMode(true);
                            setEditFloorId(floor.id);
                          }}
                        >
                          <Icon icon="fluent:edit-12-regular" height={15} width={15} />
                        </Button>
                      }
                    </ButtonGroup>
                  </div>
                ))}
              </div>
            ) : (
              <div className="floor-header d-flex border-bottom">
                Please select restaurant to see details of floors.
              </div>
            )}
            <div className="floor-layout">
              <h4 className="text-center">{floorName ? floorName : 'New Floor'}</h4>
              <Stage width={stageSize.width} height={stageSize.height}>
                <Layer>
                  <Image image={floorImage} width={stageSize.width} height={stageSize.height} />
                  {shapes.map((shape, index) => (
                    <Group
                      key={shape.id}
                      draggable = {canUpdate ? true : false}
                      x={shape.x}
                      y={shape.y}
                      onDragStart={() => setDraggedShapeId(shape.id)}
                      onDragEnd={(e) => handleDragEnd(index, e.target.x(), e.target.y())}
                      dragBoundFunc={(pos) => {
                        const minX = 0;
                        const minY = 0;
                        const maxX = stageSize.width - shape.width;
                        const maxY = stageSize.height - shape.height;

                        let newX = pos.x;
                        let newY = pos.y;
                        if (newX < minX) newX = minX;
                        if (newX > maxX) newX = maxX;
                        if (newY < minY) newY = minY;
                        if (newY > maxY) newY = maxY;

                        return {
                          x: newX,
                          y: newY,
                        };
                      }}
                    >
                      <Image
                        width={shape.width}
                        height={shape.height}
                        image={handleTableImages(shape.shape, shape.status)}
                        strokeWidth={2}
                        shadowBlur={10}
                        shadowOpacity={0.5}
                        shadowColor={'black'}
                        shadowOffset={{ x: 12, y: 12 }}
                        onClick={() => {
                          if(!canUpdate && !canDelete) return;
                          setEditTableId(shape.id);
                          setTableCreateVisible(false);
                        }}
                        onMouseEnter={(e) => {
                          const container = e.target.getStage().container();
                          container.style.cursor = 'pointer';
                        }}
                        onMouseLeave={(e) => {
                          const container = e.target.getStage().container();
                          container.style.cursor = 'default';
                        }}
                      />
                      <Text
                        text={truncateText(shape.name, 6)}
                        x={shape.width / 2}
                        y={shape.height / 2}
                        fill="black"
                        fontSize="14"
                        width={shape.width}
                        align="center"
                        offsetX={shape.width / 2}
                        offsetY={7}
                        onMouseEnter={(e) => {
                          setHoveredShapeId(shape.id);
                          const container = e.target.getStage().container();
                          container.style.cursor = 'pointer';
                        }}
                        onMouseLeave={(e) => {
                          setHoveredShapeId(null);
                          const container = e.target.getStage().container();
                          container.style.cursor = 'default';
                        }}
                        onClick={() => {
                          setEditTableId(shape.id);
                          setTableCreateVisible(false);
                        }}
                      />
                      {hoveredShapeId && hoveredShapeId === shape.id && draggedShapeId !== shape.id && (
                        <>
                          <Text
                            text={shape.name}
                            x={shape.width / 2}
                            y={shape.height / 2}
                            fill="black"
                            fontSize={14}
                            align="center"
                            height={20}
                            padding={5}
                            offsetX={shape.width / 2}
                            offsetY={-10}
                            sceneFunc={(context, shape) => {
                              context.fillStyle = '#858281';
                              context.fillRect(0, 0, shape.width(), shape.height());
                            }}
                          />
                          <Text
                            text={shape.name}
                            x={shape.width / 2}
                            y={shape.height / 2}
                            fill="white"
                            fontSize={14}
                            align="center"
                            padding={4}
                            offsetX={shape.width / 2}
                            offsetY={-10}
                          />
                        </>
                      )}
                    </Group>
                  ))}
                </Layer>
              </Stage>
            </div>
          </div>
        </div>
      </div>
    </Layout2>
  );
}

export default CreateFloor;
